<template>
  <div id="main-content">
    <section class="extended-full-width member-portal-welcome-banner" :style="welcomeBannerStyle">
      <img :src="welcomeBannerImageUrl" alt="Welcome image" />
      <div class="info-box">
        <div class="member-portal-banner-content">
          <h1>Welcome {{ currentMemberDetail.firstName }}</h1>
          <ApiLoading v-if="apiStatus.membershipInfo.loading || apiStatus.membershipInfo.fail" :apiError="apiStatus.membershipInfo.fail" isMemberPortal />
          <template v-else>
            <!-- Member number (all access) -->
            <p>
              Member number
              <br />
              <strong>{{ memberNumber }}</strong>
            </p>
            <!-- Membership Product (all access) -->
            <p v-if="!isEmptyString(membershipDetails.productName)">
              My cover
              <br />
              <strong>{{ membershipDetails.productName }}</strong>
            </p>
          </template>
        </div>
      </div>
    </section>
    <Alert v-if="showAlert && !apiStatus.refData.fail && !apiStatus.refData.loading" alertType="info" :message="alertText" class="member-portal-alert" />
    <ApiLoading v-else-if="apiStatus.refData.loading && !apiStatus.refData.fail" :apiError="apiStatus.refData.fail" isMemberPortal class="member-portal-alert" />
    <Alert v-else-if="apiStatus.refData.fail && !apiStatus.refData.loading" alertType="error" class="member-portal-alert" />
    <div class="mp-content">
      <TopTasks
        :topTasksOptions="topTasksOptions"
        :topTasksInformationText="topTasksInformationText"
        :apiStatus="apiStatus"
        :membershipDetails="membershipDetails"
        :isFullAccess="isFullAccess"
        :currentIsExtrasFlag="currentIsExtrasFlag"
        :hasAccessToTaxPage="hasAccessToTaxPage"
        :class="showAlert || (apiStatus.refData.loading && !apiStatus.refData.fail) || (apiStatus.refData.fail && !apiStatus.refData.loading) ? 'feature-landing-page-msg' : ''"
      ></TopTasks>
      <section class="mp-section">
        <div class="left">
          <h2 class="h4 home-section-title">Update details</h2>
          <div class="flex-wrap-section">
            <div class="mp-card">
              <h3 class="h6">Update your details</h3>
              <p>Take a few moments to make sure we can get in touch by checking your</p>
              <ul>
                <li>Communication preferences</li>
                <li>Phone number</li>
                <li>Email address</li>
              </ul>
              <router-link
                class="button secondary"
                :to="{
                  name: MY_DETAILS_VIEW_UPDATE_DETAILS,
                  params: { personCode: currentMemberDetail.personCode, name: `${currentMemberDetail.firstName} ${currentMemberDetail.lastName}` },
                  query: { step: 1 },
                }"
              >
                Update your details
              </router-link>
            </div>
            <div class="mp-card">
              <h3 class="h6">Change cover</h3>
              <p>Whether you are planning a family, looking for more back on extras or need the security of comprehensive hospital, we have a cover option for you.</p>
              <router-link
                class="button secondary"
                :to="{
                  name: CHANGE_MY_COVER,
                }"
              >
                Cover details
              </router-link>
            </div>
            <div class="mp-card">
              <h3 class="h6">Update accounts</h3>
              <p>Keep your direct credit account up to date to ensure we can get your money to you quickly when you make a claim.</p>
              <router-link
                class="button secondary"
                :to="{
                  name: PAYMENT_DETAILS,
                }"
              >
                Account details
              </router-link>
            </div>
            <div v-if="currentMemberDetail.memberItems && currentMemberDetail.memberItems.showAccountSecurityMenu" class="mp-card">
              <h3 class="h6">Account security</h3>
              <p>Manage the contact details used to keep your account secure.</p>
              <a class="button secondary" href="/MemberPortal/GetRequestAccountManagement" target="_blank"> Manage security settings </a>
            </div>
          </div>
        </div>
        <div class="right">
          <ApiLoading v-if="apiStatus.membershipInfo.fail || apiStatus.membershipInfo.loading" :apiError="apiStatus.membershipInfo.fail" isMemberPortal />
          <div v-else>
            <h2 class="h4 home-section-title">Policy details</h2>
            <p v-if="!isEmptyString(membershipDetails.productName)">
              <strong>{{ membershipDetails.productName }}</strong>
            </p>
            <!-- product guide link -->
            <p v-if="!apiStatus.refData.fail && !apiStatus.refData.loading" v-html="topTasksInformationText"></p>
            <a
              v-if="!apiStatus.membershipInfo.fail && !apiStatus.membershipInfo.loading && !isEmptyString(membershipDetails.productGuideLink)"
              :href="membershipDetails.productGuideLink"
              aria-label="download product guide link"
              target="_blank"
            >
              Download {{ membershipDetails.productName }} product guide
            </a>
          </div>
        </div>
      </section>
      <section class="feature-landing-page">
        <h2 class="h4 home-section-title">Members</h2>
        <ApiLoading v-if="apiStatus.membersData.fail || apiStatus.membersData.loading" :apiError="apiStatus.membersData.fail" isMemberPortal />
        <ol v-else class="member-wrapper">
          <li class="member" v-for="member in members" :key="`${member.firstName}-${member.lastName}`">
            <MemberTag :firstName="member.firstName" :lastName="member.lastName" :relationship="member.coveredOnMembership" />
          </li>
        </ol>
      </section>

      <ApiLoading class="api-loading" v-if="apiStatus.refData.loading || apiStatus.refData.fail" :apiError="apiStatus.refData.fail" :isMemberPortal="isMemberPortal" />
      <!-- Carousel commented out during UI-Uplift -->
      <!-- <Carousel v-else-if="isCarouselShown" :carouselArray="carouselArray" :apiStatus="apiStatus"></Carousel> -->
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import orderBy from 'lodash.orderby';
import { isBefore } from 'date-fns';
import { isEmptyString } from '@general/js/utils';
import HomePageServices from '@general/js/services/HomePage';
// import Carousel from '@general/js/components/Carousel.vue';
import { NonDependantFlexCodes, AccessLevel } from '@/enums';
import { VUEX_GENERAL, VUEX_MEMBER_DETAILS } from '@/constants/VuexConstants';
import { MY_DETAILS_VIEW_UPDATE_DETAILS, CHANGE_MY_COVER, PAYMENT_DETAILS } from '@/router/routeNames';
import { TopTasks, MemberTag, ApiLoading, Alert } from '@/components';
import { getAccessLevel, getCoveredAs, formatKenticoLink } from '@/utils';
import MemberDetailsServices from '@/services/MemberDetails';
import { PolicyStatus } from '@general/js/enums';
import { convertDateStringToText } from '@general/js/utils/dateTimeUtils';
import { clearSessionObject, fetchSessionObject } from '@/utils/SessionStorageUtils';
import { STORAGE_KEYS } from '@general/js/utils/SessionStorageUtils';

export default {
  name: 'Home',

  components: {
    // Carousel,
    ApiLoading,
    TopTasks,
    MemberTag,
    Alert,
  },

  data() {
    return {
      isMemberPortal: true,
      showAlert: false,
      // showCarousel: false,
      // carouselArray: [],
      alertText: '',
      topTasksInformationText: '',
      topTasksOptions: [],
      welcomeBannerImageUrl: '',
      maxNumberOfSlide: 5, // Maximum number of displayed slides
      apiStatus: {
        refData: {
          fail: false,
          loading: false,
        },
        membershipInfo: {
          fail: false,
          loading: false,
        },
        membersData: {
          fail: false,
          loading: false,
        },
      },
      members: [],
      membershipDetails: {},
      nonDependantList: Object.values(NonDependantFlexCodes),
      MY_DETAILS_VIEW_UPDATE_DETAILS,
      CHANGE_MY_COVER,
      PAYMENT_DETAILS,
      memberNumberInEmailQuoteURL: null,
    };
  },

  beforeMount() {
    this.resetPolicyNumber();
    this.getReferenceData();
    this.getMembersData();
    this.getMembershipData();
    // Redirecting to link that was saved in session storage if it exists AND logged in user has PH access
    if (fetchSessionObject(STORAGE_KEYS.REDIRECT_LINK)) {
      if (this.accessLevel === AccessLevel.POLICY_HOLDER_ACCESS) {
        this.apiStatus.refData.loading = true;
        window.location.href = formatKenticoLink(fetchSessionObject(STORAGE_KEYS.REDIRECT_LINK));
      }
      clearSessionObject(STORAGE_KEYS.REDIRECT_LINK);
    }

    // Redirecting to email quote link that was saved in session storage if it exists AND logged in user has PH access AND link matches memberNumber
    if (fetchSessionObject(STORAGE_KEYS.EMAIL_QUOTE_REDIRECT_LINK)) {
      this.saveEmailQuoteURLMemberNumber();
      if (this.accessLevel === AccessLevel.POLICY_HOLDER_ACCESS && this.memberNumber === this.memberNumberInEmailQuoteURL) {
        this.apiStatus.refData.loading = true;
        window.location.href = formatKenticoLink(fetchSessionObject(STORAGE_KEYS.EMAIL_QUOTE_REDIRECT_LINK));
      }
      clearSessionObject(STORAGE_KEYS.EMAIL_QUOTE_REDIRECT_LINK);
    }
  },

  computed: {
    ...mapGetters({
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      currentMemberDetail: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
      memberships: VUEX_GENERAL.GETTERS.memberships,
      currentIsExtrasFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyIsExtrasFlag,
    }),

    accessLevel() {
      return getAccessLevel(this.memberNumber, this.memberships)?.toLowerCase();
    },

    isFullAccess() {
      return this.accessLevel === AccessLevel.FULL_ACCESS || this.accessLevel === AccessLevel.POLICY_HOLDER_ACCESS;
    },

    coveredAs() {
      return getCoveredAs(this.memberNumber, this.memberships)?.toUpperCase();
    },

    currentMemberFullName() {
      return `${this.currentMemberDetail.firstName} ${this.currentMemberDetail.lastName}`;
    },

    hasAccessToTaxPage() {
      // Covered as Policy Holder or Spouse (and any of the partner type)
      if (this.nonDependantList.includes(this.coveredAs)) {
        return true;
      }
      // Covered as Dependants (and any of the dependant types) and has Full Access
      if (!this.nonDependantList.includes(this.coveredAs) && this.accessLevel === AccessLevel.FULL_ACCESS) {
        return true;
      }
      return false;
    },

    // isCarouselShown() {
    //   if (this.showCarousel === true && this.carouselArray !== undefined && this.carouselArray.length !== 0) {
    //     return true;
    //   }
    //   return false;
    // },

    isActive() {
      return this.membershipDetails?.policyStatus === PolicyStatus.ACTIVE;
    },

    isSuspended() {
      return this.membershipDetails?.policyStatus === PolicyStatus.SUSPENDED;
    },

    getPremiumsText() {
      // IF new member that has not yet paid
      if (this.membershipDetails.datePaidTo === null) {
        return ' however your first premium has not been paid.';
      }

      // IF not financial, defined as datePaidTo is a date in the past
      if (isBefore(new Date(this.membershipDetails.datePaidTo), new Date())) {
        return 'Premiums are in arrears.';
      }

      return '';
    },

    paymentFrequency() {
      return this.membershipDetails?.frequency?.toLowerCase();
    },

    linkToContactUs() {
      return `/portal/${this.memberNumber}/support/contact-us`;
    },

    welcomeBannerStyle() {
      return { background: `linear-gradient(135deg, #eae0d6 63%, transparent 37%), url(${this.welcomeBannerImageUrl})` };
    },
  },

  methods: {
    ...mapMutations([
      VUEX_GENERAL.MUTATIONS.resetPolicy,
      VUEX_MEMBER_DETAILS.MUTATIONS.resetMemberDetails,
      VUEX_GENERAL.MUTATIONS.saveThisPolicyNumber,
      VUEX_GENERAL.MUTATIONS.updateMembers,
      VUEX_GENERAL.MUTATIONS.updateMembershipInfo,
    ]),

    ...mapMutations('MemberDetails', ['resetMemberDetails']),

    resetPolicyNumber() {
      this.resetMemberDetails();
      this.resetPolicy();
      const policyNumber = this.$route.params.policyNumber;
      this.saveThisPolicyNumber(policyNumber);
    },

    sortByFirstName(members) {
      return orderBy(members, ['firstName'], ['asc']);
    },

    async getReferenceData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        const res = await HomePageServices.getMPReferenceData();
        this.showAlert = res?.showAlert;
        this.alertText = res?.alertText?.informationText;
        this.topTasksInformationText = res?.topTasksInformationText?.informationText;
        this.topTasksOptions = JSON.parse(JSON.stringify(res?.topTasksOptions).tokenSwap());
        // this.showCarousel = res?.showCarousel;
        // Only display top 5 slides
        // this.carouselArray = res?.carousels.slice(0, this.maxNumberOfSlide);
        this.welcomeBannerImageUrl = res?.welcomeBannerImage.informationText.replace(/^~/, '');
      } catch (error) {
        logger.log(error);
        this.apiStatus.refData.fail = true;
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async getMembersData() {
      this.apiStatus.membersData.loading = true;
      this.apiStatus.membersData.fail = false;

      try {
        const members = await MemberDetailsServices.getMembers(this.$route.params.policyNumber);

        // Save member list in Vuex store to use in other pages
        this.updateMembers(members);

        // [POLICY HOLDER, SPOUSE, DEFACTO, EX SPOUSE]
        const nonDependantList = Object.keys(NonDependantFlexCodes).map((str) => {
          return str.replace(/_/i, ' ');
        });

        // filter non dependant members and marked as Partner
        const nonDependants = members.filter((obj) => {
          if (obj.coveredOnMembership.toUpperCase() === 'POLICY HOLDER') {
            return false;
          }

          return nonDependantList.includes(obj.coveredOnMembership.toUpperCase());
        });

        // filter dependant members and marked as Child
        const dependants = members.filter((obj) => {
          return !nonDependantList.includes(obj.coveredOnMembership.toUpperCase());
        });

        // find policy holder
        const policyHolder = members.find((obj) => {
          return obj.coveredOnMembership.toUpperCase() === 'POLICY HOLDER';
        });

        this.members = [policyHolder, ...this.sortByFirstName(nonDependants), ...this.sortByFirstName(dependants)];
        this.apiStatus.membersData.fail = false;
      } catch (error) {
        logger.error(error);
        this.apiStatus.membersData.fail = true;
      } finally {
        this.apiStatus.membersData.loading = false;
      }
    },

    async getMembershipData() {
      this.apiStatus.membershipInfo.loading = true;
      this.apiStatus.membershipInfo.fail = false;

      const currentMemberNumber = this.$route.params.policyNumber;

      try {
        const res = await HomePageServices.getMembershipInfo(currentMemberNumber);
        this.updateMembershipInfo(res);
        this.membershipDetails = { ...res, memberFullName: this.currentMemberFullName ?? '', policyStatus: res.policyStatus ?? null };
      } catch (error) {
        logger.log(error);
        this.apiStatus.membershipInfo.fail = true;
      } finally {
        this.apiStatus.membershipInfo.loading = false;
      }
    },

    isEmptyString,
    convertDateStringToText,

    saveEmailQuoteURLMemberNumber() {
      const redirectLink = formatKenticoLink(fetchSessionObject(STORAGE_KEYS.EMAIL_QUOTE_REDIRECT_LINK));
      const memberNumberInEmailQuoteURL = redirectLink.match(/portal\/(\d+)\//);
  
      if (memberNumberInEmailQuoteURL && memberNumberInEmailQuoteURL[1]) {
        this.memberNumberInEmailQuoteURL = memberNumberInEmailQuoteURL[1];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.home-section-title) {
  border-bottom: 1px solid $GREY_300;
}
.extended-full-width::before {
  content: '';
  background: inherit;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: right top;
  height: 100%;
  position: absolute;
  right: 50%;
  top: 0;
  transform: translateX(50%);
  width: 100vw;
  z-index: -1;

  @include widescreen {
    background-size: unset;
  }

  @include tablet {
    background-size: unset;
    background-position: center top;
  }
}

.api-loading-wrapper {
  margin-bottom: rem(50);

  @include mobile {
    margin-bottom: rem(30);
  }
}

.extended-full-width {
  background: $GREY_50;
  margin-top: rem(-50);

  .info-box {
    @include main-content;
    padding-top: 4.5rem;
    padding-bottom: 2.5rem;
    z-index: 3;

    @include tablet {
      padding-left: 0 !important;
    }

    @include mobile {
      padding-top: 3.5rem;
    }
  }

  h1 {
    margin-bottom: rem(15);
  }

  p {
    margin-bottom: rem(10);

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.mp-content {
  @include flex-col;
  margin: 3rem 0;
  row-gap: 2rem;

  @include mobile {
    margin-top: 1.5rem;
  }
}

.member-wrapper {
  @include grid-layout;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  column-gap: rem(40);
  row-gap: rem(30);

  @include mobile {
    @include flex-col;
  }
}

:deep(.mp-section) {
  display: flex;
  flex-direction: row;
  width: 100%;
  column-gap: rem(56);

  .left {
    flex: 2;
  }

  .right {
    @include flex-col;
    flex: 1.5;
  }

  @include mobile {
    flex-direction: column;
    row-gap: 2rem;
  }
}

:deep(.flex-wrap-section) {
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.5rem;
  row-gap: 0.5rem;

  > * {
    flex: 0 1 49%;
  }

  @include tablet-and-below {
    @include flex-col;

    * {
      flex: unset;
    }
  }

  .mp-card {
    @include flex-col;
    box-sizing: border-box;
    border: 1px solid $GREY_200;
    border-radius: 4px;
    padding: 1.5rem;

    p {
      flex-grow: 1;
    }
  }
}

.swiper {
  margin-bottom: 0;

  @include mobile {
    margin-top: rem(50);
  }
}
</style>
